import authStore from "@/stores/authStore";
import { CacheProvider } from "@emotion/react";
import { Grid } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import createCache from "@emotion/cache";
import "./digital.wallet.css";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import DigitalWalletManagementFilter from "@/components/digitalWallet/digital.wallet.management.filter";
import popupService from "@/services/popupService";
import {
    DigitalWalletManagementFilterRequest,
    DigitalWalletManagementFilterResponse,
    DigitalWalletManagementRowResult,
} from "@/models/digital.wallet";
import digitalWalletStore from "@/stores/digital.wallet.store";
import DigitalWalletManagementOptionBar from "@/components/digitalWallet/digital.wallet.management.option.bar";
import DigitalWalletManagementGrid from "@/components/digitalWallet/digital.wallet.management.grid";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { PermissionName } from "@/models/apiModels";
import { checkPermissionsWithAlert } from "@/util";
import { useTranslation } from "react-i18next";
import filtersStore from "@/stores/filtersStore";
import api from "@/services/api";
import TotalCountComponent from "@/components/common/resultCountComponent";
import to from "await-to-js";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const DigitalWalletManagementPage: FC = () => {
    const { t } = useTranslation();
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [digitalWallets, setDigitalWallets] = useState<DigitalWalletManagementRowResult[]>([]);
    const [filterOrderBy, setFilterByOrderBy] = useState<string>("Id");
    const [filterAsc, setFilterAsc] = useState<boolean>(true);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [filter, setFilter] = useState<DigitalWalletManagementFilterRequest>(
        new DigitalWalletManagementFilterRequest()
    );
    const [waletSelectionModel, setWalletSelectionModel] = useState<GridRowSelectionModel>([]);

    async function getDigitalWalletsManagement(): Promise<DigitalWalletManagementFilterResponse> {
        const filterStore = filtersStore.digitalWalletManagmentFilterData;
        if (filterStore != null) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setFilter(filterStore ?? new DigitalWalletManagementFilterRequest());
            const responsePromise = api.getDigitalWalletsManagement(
                filterStore ?? new DigitalWalletManagementFilterRequest()
            );
            return (await responsePromise).data;
        }

        filtersStore.setDigitalWalletManagmentFilterData(filter);
        const responsePromise = api.getDigitalWalletsManagement(filter);
        return (await responsePromise).data;
    }

    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.WalletManagment)) {
            return;
        }
        setIsSearch(() => true);
        try {
            popupService.showLoading();
            const data = await getDigitalWalletsManagement();
            const result = data;
            if (result) {
                setHasNext(result.hasNext);
                if (filter.pageIndex === 0) {
                    setDigitalWallets(result.rows);
                    digitalWalletStore.setDigitalWalletSums(
                        result.total,
                        result.totalFilter,
                        result.currentSubsidyBalanceOfAllEmployees,
                        result.currentBalanceInActiveWallets,
                        result.unpaidSubsidies,
                        result.subsidyBalance,
                        result.subsidyUsedPercentNotification
                    );
                } else {
                    setDigitalWallets((old) => [...old, ...result.rows]);
                }
                digitalWalletStore.setDigitalWalletsStore(result.rows);
                setIsSearch(() => false);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            setIsSearch(() => false);
        }
    }

    const searchDigitalWalletsClick = () => {
        setDigitalWallets([]);
        filtersStore.setDigitalWalletManagmentFilterData(null);
        setPageIndex(0);
        setSearchCounter((e) => e + 1);
    };

    const setPageIndex = (index?: number) => {
        setFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? filter.pageIndex + 1,
        }));
    };

    async function sortBy(sortBy: string) {
        if (filterOrderBy !== sortBy) {
            setFilterAsc(true);
        } else {
            setFilterAsc(!filterAsc);
        }
        setPageIndex(0);
        setDigitalWallets([]);
        setFilterByOrderBy(sortBy);
    }

    const generateCsv = async () => {
        popupService.showLoading();
        const [err, resp] = await to(digitalWalletStore.exportDigitalWallets(filter));
        if (err) {
            popupService.showApiError(err);
            return;
        }

        const response = resp!;
        if (response.success && response.interfaceId !== 0) {
            popupService.downloadFile(
                t("general.ExportToXLS?"),
                api.generateDigitalWalletsExportLink(response.interfaceId)
            );
        } else {
            popupService.showError("Unhandled exception");
            return;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [filter.pageIndex, filterOrderBy, filterAsc, searchCounter]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithAlert(PermissionName.WalletManagment) ? (
                <Grid className="digital-wallet-main-block">
                    <DigitalWalletManagementOptionBar
                        searchClick={searchDigitalWalletsClick}
                        waletSelectionModel={waletSelectionModel}
                        setWalletSelectionModel={setWalletSelectionModel}
                        exportCsv={generateCsv}
                    />

                    <DigitalWalletManagementFilter
                        searchClick={searchDigitalWalletsClick}
                        filter={filter}
                        setFilter={setFilter}
                    ></DigitalWalletManagementFilter>

                    <TotalCountComponent
                        allCount={digitalWalletStore.totalCount}
                        currentCount={digitalWalletStore.totalFilter}
                        title={t("digitalWallet.QuantityOfWallets") as string}
                    />
                    <DigitalWalletManagementGrid
                        sortBy={sortBy}
                        wallets={digitalWallets}
                        isSearch={isSearch}
                        fetchMore={setPageIndex}
                        hasNext={hasNext}
                        waletSelectionModel={waletSelectionModel}
                        setWalletSelectionModel={setWalletSelectionModel}
                    ></DigitalWalletManagementGrid>
                </Grid>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default DigitalWalletManagementPage;
